import { FC, useState } from 'react';
import moment from 'moment-timezone';
import { useMutation } from '@apollo/client';
import { ListGroup } from 'react-bootstrap';
import Switch from 'components/common/Switch/Switch';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Drawer from '@mui/material/Drawer';
import ElementWithTooltip from 'components/common/ElementWithTooltip/ElementWithTooltip';
import IconButton from 'components/common/IconButton/IconButton';
import ConfirmDialog from 'components/common/Dialog/ConfirmDialog';
import Link from 'components/common/Link/Link';
import { FilterValueForm } from 'components/billingAndUsage/components/Filter/FilterValueForm';
import { FilterDisableForm } from 'components/billingAndUsage/components/Filter/FilterDisableForm';
import { DELETE_FILTER, GET_BILLING_AND_USAGE_DATA, UPDATE_FILTER } from 'components/billingAndUsage/queries';
import { BudgetCategoryDiscardFilterType, DiscardFilterType } from 'components/billingAndUsage/types';
import ELEMENT from 'common/element';

import './Filter.scss';

type FilterProps = {
  title: string;
  categoryId: string;
  logFilter: string;
  filters: BudgetCategoryDiscardFilterType[];
};

export const Filter: FC<FilterProps> = ({ title, logFilter, filters, categoryId }: FilterProps) => {
  const [deleteFilter] = useMutation(DELETE_FILTER, {
    refetchQueries: [
      {
        query: GET_BILLING_AND_USAGE_DATA,
      },
    ],
  });
  const [updateFilter] = useMutation(UPDATE_FILTER, {
    refetchQueries: [
      {
        query: GET_BILLING_AND_USAGE_DATA,
      },
    ],
  });

  const filterInitialState: DiscardFilterType = {
    id: '',
    filterValue: '',
    disabledUntil: '',
    categoryId,
    filterName: title,
  };
  const [currentFilter, setCurrentFilter] = useState<DiscardFilterType>(filterInitialState);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [drawerState, setDrawerState] = useState(false);
  const [drawerSwitchFilterState, setDrawerSwitchFilterState] = useState(false);

  const handleShowConfirmDialog = (filter: DiscardFilterType): void => {
    setCurrentFilter(filter);
    setShowConfirmDialog(true);
  };

  const handleCloseConfirmDialog = (): void => {
    setShowConfirmDialog(false);
  };

  const handleDrawerOpen = (filter: DiscardFilterType): void => {
    setCurrentFilter(filter);
    setDrawerState(true);
  };

  const handleDrawerClose = (): void => {
    setDrawerState(false);
    setDrawerSwitchFilterState(false);
  };

  const handleSwitchFilterDrawerOpen = (filter: DiscardFilterType): void => {
    setCurrentFilter(filter);
    setDrawerSwitchFilterState(true);
  };

  const handleDeleteFilter = (): void => {
    deleteFilter({ variables: { categoryId, filterId: currentFilter.id } });
    setCurrentFilter(filterInitialState);
  };

  const handleUpdateFilter = (filter: DiscardFilterType): void => {
    updateFilter({
      variables: {
        filterId: filter.id,
        categoryId: filter.categoryId,
        filterRequest: { filterValue: filter.filterValue, disabledUntil: filter.disabledUntil },
      },
    });
  };

  const enableFilter = (filter: DiscardFilterType): void => {
    filter.disabledUntil = new Date(0).toISOString();
    handleUpdateFilter(filter);
  };

  const handleChangeFilterSwitch = (filter: DiscardFilterType, isFilterEnabled: boolean): void => {
    if (isFilterEnabled) {
      handleSwitchFilterDrawerOpen(filter);
    } else {
      enableFilter(filter);
    }
  };

  const getLogFileLink = (filterValue: string): string =>
    `/events?filter=${encodeURIComponent(`(${logFilter}) (${filterValue})`)}`;
  // if dateStr is undefined or null, the filter must be disabled
  const isDiscardFilterEnabled = (disabledUntil: string | null): boolean =>
    !!(disabledUntil && moment(disabledUntil) < moment());

  const renderItems = filters.map((filter: BudgetCategoryDiscardFilterType) => {
    const filterData = { ...filter, filterValue: filter.value, filterName: title };
    const isFilterEnabled = isDiscardFilterEnabled(filter.disabledUntil);
    const disabledStyle = isFilterEnabled ? '' : 'Filter__Item--Disabled';
    return (
      <ListGroup.Item key={filter.id} className={disabledStyle} data-test-id={ELEMENT.planPageLogCategoryDiscardFilter}>
        <div className="Filter__Item DiscardFilter">
          <Switch
            checked={isFilterEnabled}
            onChange={(): void => handleChangeFilterSwitch(filterData, isFilterEnabled)}
            testId={ELEMENT.planPageDiscardFilterSwitchOnOff}
          />
          <span className="DiscardFilter__Title">{filter.value}</span>
          <Link
            href={getLogFileLink(filter.value)}
            openInNewTab
            className="DiscardFilter__Icon"
            testId={ELEMENT.planPageDiscardFilterOpenSearchButton}
          >
            <ElementWithTooltip tooltip="View Logs">
              <SearchIcon />
            </ElementWithTooltip>
          </Link>
          <IconButton
            tooltip="Edit"
            onClick={(): void => handleDrawerOpen(filterData)}
            icon={<EditIcon />}
            className="DiscardFilter__Icon"
            testId={ELEMENT.planPageDiscardFilterEditButton}
          />
          <IconButton
            tooltip="Delete"
            onClick={(): void => handleShowConfirmDialog(filterData)}
            icon={<DeleteIcon />}
            className="DiscardFilter__Icon"
            testId={ELEMENT.planPageDiscardFilterDeleteButton}
          />
        </div>
      </ListGroup.Item>
    );
  });

  return (
    <>
      <ListGroup className="Filter__List" data-test-id={ELEMENT.planPageLogCategoryDiscardFilters}>
        <ListGroup.Item className="Filter__Title">{`${title} Category - Discard Filters`}</ListGroup.Item>
        {renderItems}
        <ListGroup.Item>
          <button
            type="button"
            className="Filter__Link"
            onClick={(): void => handleDrawerOpen(filterInitialState)}
            data-test-id={ELEMENT.planPageCreateDiscardFilter}
          >
            Add New Filter...
          </button>
        </ListGroup.Item>
      </ListGroup>
      <Drawer anchor="right" open={drawerState} onClose={handleDrawerClose} className="CurrentBillingSettings__Drawer">
        <FilterValueForm filter={currentFilter} onClose={handleDrawerClose} />
      </Drawer>
      <Drawer
        anchor="right"
        open={drawerSwitchFilterState}
        onClose={handleDrawerClose}
        className="CurrentBillingSettings__Drawer"
      >
        <FilterDisableForm
          filterName={currentFilter.filterName}
          onClose={handleDrawerClose}
          onSubmit={(disabledUntil): void => handleUpdateFilter({ ...currentFilter, disabledUntil })}
        />
      </Drawer>
      <ConfirmDialog
        title="Confirm"
        text="Are you sure you want to delete this filter? This cannot be undone."
        show={showConfirmDialog}
        close={handleCloseConfirmDialog}
        onSubmit={handleDeleteFilter}
      />
    </>
  );
};
